import { Avatar, Chip, Stack, Typography } from '@mui/material'
import { Divider, SkeletonText } from 'utoniq-ui'

import {
  Artist,
  Maybe,
  NotificationBoxFragment,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import { NotificationBoxSkeleton } from '@/components/organisms/notification'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { LinkifyBox } from '@/lib/reactLinkify'

export * from './fragments'

export type State = {
  loading: boolean
  notification: Maybe<NotificationBoxFragment>
}

type NotificationTitleCellProps = {
  loading: State['loading']
  title: Maybe<NotificationBoxFragment['title']>
  date: Maybe<NotificationBoxFragment['date']>
  official: boolean
  artist?: Maybe<Pick<Artist, 'avatar' | 'displayName'>>
}

type NotificationContentsTextCellProps = {
  loading: State['loading']
  body: Maybe<NotificationBoxFragment['body']>
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {}

export const NotificationDetailTemplate: Template<State, Action> = ({
  state: { notification, loading },
}) => {
  const { t } = useTranslation('notificationDetail')
  const { routerNames, generatePath, routerPages } = useRouter()
  return (
    <BaseLayout
      header={{
        title: t('title'),
        back: true,
        defaultBackPage: generatePath(routerPages.Notifications),
      }}
    >
      <Head title={routerNames.NotificationDetail} />
      <NotificationTitleCell
        loading={loading}
        title={notification?.title ?? null}
        official={false}
        date={notification?.formattedDate ?? null}
      />
      <NotificationContentsTextCell
        loading={loading}
        body={notification?.body ?? null}
      />
    </BaseLayout>
  )
}

export const NotificationTitleCell: Cell<NotificationTitleCellProps> = ({
  loading,
  official,
  title,
  date,
  artist,
}) => {
  const { t } = useTranslation('notifications')
  if (loading) {
    return <NotificationBoxSkeleton />
  }
  return (
    <CellFrame sx={{ px: 5, position: 'relative' }}>
      {official && (
        <Stack direction="row" gap={2}>
          {artist?.avatar?.smallUri && (
            <Avatar
              src={artist?.avatar?.smallUri}
              sx={{ height: 20, width: 20 }}
            />
          )}
          <Chip
            size="small"
            sx={{
              height: 20,
              borderRadius: '2px',
              bgcolor: 'secondary.dark',
              py: 1,
              '.MuiChip-label': {
                px: 1,
              },
            }}
            label={
              <Typography color="white" variant="subtitle2">
                {t('official')}
              </Typography>
            }
          />
        </Stack>
      )}
      <Typography variant="h2" sx={{ pt: 2, wordBreak: 'break-word' }}>
        {title}
      </Typography>
      <Typography
        variant="subtitle2"
        sx={{
          pt: 2,
          pb: 5,
          color: 'grey.600',
        }}
      >
        {date}
      </Typography>
      <Divider sx={{ bgcolor: 'grey.100' }} />
    </CellFrame>
  )
}

export const NotificationContentsTextCell: Cell<NotificationContentsTextCellProps> =
  ({ loading, body }) => (
    <CellFrame
      sx={{
        pt: 6,
        pb: 4,
        px: 5,
        userSelect: 'text',
        WebkitUserSelect: 'text',
        msUserSelect: 'text',
        position: 'relative',
      }}
    >
      {loading ? (
        <SkeletonText variant="small" lastWidth="75%" numberOfLine={5} />
      ) : (
        <LinkifyBox>
          <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
            {body}
          </Typography>
        </LinkifyBox>
      )}
    </CellFrame>
  )
